import React, { useLayoutEffect, useEffect, useState, useCallback } from 'react';
import { createPortal } from 'react-dom';
import './TemplateTooltip.scss';

/**
 * Renders a tooltip via a portal attached to document.body so it is never
 * clipped by overflow or stacking contexts on ancestor elements.
 *
 * Props:
 *   anchorRef  — ref attached to the card element used to calculate position
 *   visible    — boolean controlling visibility
 *   title      — template name (bold heading)
 *   description — template description text
 */
const TemplateTooltip = ({ anchorRef, visible, title, description }) => {
    const [pos, setPos] = useState({ top: 0, left: 0 });

    const updatePos = useCallback(() => {
        if (anchorRef?.current) {
            const rect = anchorRef.current.getBoundingClientRect();
            setPos({
                top: rect.top - 8,
                left: rect.left + rect.width / 2,
            });
        }
    }, [anchorRef]);

    useLayoutEffect(() => {
        if (visible) updatePos();
    }, [visible, updatePos]);

    useEffect(() => {
        if (!visible) return;

        // Collect all scrollable ancestors so we can track any scroll container
        const scrollableParents = [];
        let el = anchorRef?.current?.parentElement;
        while (el) {
            const overflowY = window.getComputedStyle(el).overflowY;
            if (overflowY === 'auto' || overflowY === 'scroll') {
                scrollableParents.push(el);
            }
            el = el.parentElement;
        }
        window.addEventListener('scroll', updatePos, { passive: true });
        scrollableParents.forEach(p => p.addEventListener('scroll', updatePos, { passive: true }));

        return () => {
            window.removeEventListener('scroll', updatePos);
            scrollableParents.forEach(p => p.removeEventListener('scroll', updatePos));
        };
    }, [visible, anchorRef, updatePos]);

    if (!visible || (!title && !description)) return null;

    return createPortal(
        <div
            className="template-tooltip-portal"
            style={{ top: pos.top, left: pos.left }}
        >
            {title && <strong className="template-tooltip-title">{title}</strong>}
            {description && (
                <>
                    <br />
                    <br />
                    <span className="template-tooltip-description">{description}</span>
                </>
            )}
        </div>,
        document.body
    );
};

export default TemplateTooltip;
